<template>
  <!-- If this works, it should be it's own component me thinks -->
  <!-- TODO: consider making text white-space styles and pointer events configurable -->
  <div
    v-if="!editable"
    ref="editor"
    class="display-only-text"
    :class="noWrapClass"
    :style="dataTextStyles"
    v-html="text"
  />
  <div
    v-else
    ref="editor"
    class="contenteditable-editor"
    :class="noWrapClass"
    contenteditable
    spellcheck="false"
    @input="onEditorChange"
    @keydown="onKeyDown"
    @blur="onBlur"
    @focus="onFocus"
    @mouseup="onMouseUp"
    v-html="content"
  />
</template>

<script>
import { debounce } from 'lodash-es';
import { mapGetters } from 'vuex';
import EventBus from '../utils/event-bus';

/**
 * Reset cursor to start of element
 * @param {Selection} sel
 * @param {DOMElement} el
 */
const resetCursor = (sel, el) => {
  const range = document.createRange();
  range.setStart(el, 0);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
};

export default {
  emits: ['update:font-size', 'update:text', 'resizing'],
  props: {
    fontSize: { type: [Number, String], default: null },
    element: { type: Object, required: true },
    editing: { type: Boolean, default: false },
    editable: { type: Boolean, default: true },
    text: { type: [String, Number], default: '' },
    dataProvider: { type: Object, default: null },
    isSelected: { type: Boolean, default: false },
  },

  data() {
    return {
      isEditing: false,
      content: this.text,

      previousFontSize: null,

      // Used as a hack to track updating state
      // and prevent infinite loops since we're reliant on global event listeners
      makingChanges: false,
      suppressNextStyleResize: false,
      suppressedFontSize: null,
      fineTuneIterations: 0,
      fineTuneMaxIterations: 1500,
    };
  },

  computed: {
    ...mapGetters('graphicsTemplate', [
      'getCanvas',
      'isPropertyMapped',
    ]),

    ...mapGetters('panAndZoom', { scaleFactor: 'getScaleFactor' }),

    _fontSize: {
      get() {
        return this.fontSize;
      },
      set(v) {
        this.$emit('update:font-size', v);
      },
    },

    needsNoWrap() {
      // Allow wrap while editing to fix Firefox bug
      if (this.element.styles.textFlowOption === 2) {
        return false;
      }

      return true;
    },

    noWrapClass() {
      return this.needsNoWrap ? 'editor-no-wrap' : '';
    },

    characterCount() {
      const div = document.createElement('div');
      div.innerHTML = this.element.text;
      return div.textContent.length;
    },

    editorEl() {
      return this.$refs.editor;
    },

    isSocial() {
      return this.dataProvider.type === 'social-data';
    },

    dataTextStyles() {
      if (this.isSocial && this.element.network !== 'reddit') {
        return 'white-space: pre-wrap; pointer-events: none;';
      }

      return 'pointer-events: none;';
    },

    xPadding() {
      return (this.element.padding?.left ?? 0) + (this.element.padding?.right ?? 0);
    },
    yPadding() {
      return (this.element.padding?.top ?? 0) + (this.element.padding?.bottom ?? 0);
    },

    shouldAutoResize() {
      return this.isSelected || Boolean(this.element?.data?.dataListId);
    },
  },
  watch: {
    editing(isEditing) {
      if (isEditing) {
        this.editorEl.focus();
      } else {
        this.editorEl.blur();
      }
    },

    text(val) {
      if (val !== this.content && !this.isEditing) {
        this.content = val;
        this.editorEl.innerHTML = val;
        this.resizeText();
      }
    },

    'element.fitText': {
      handler(newVal, oldVal) {
        if (newVal === oldVal) {
          return;
        }

        // store/restore previous font size when entering/leaving autoscale
        if (newVal) {
          this.previousFontSize = this._fontSize;
        } else if (oldVal) {
          this._fontSize = this.previousFontSize;
        }
      },
    },

    // resize whenever styles change (i.e. letter spacing)
    'element.styles': {
      deep: true,
      handler() {
        // parseFloat handles CSS-like values (e.g. "12px") where Number(...) becomes NaN.
        const currentFontSize = Number.parseFloat(this.element?.styles?.fontSize);
        if (
          this.suppressNextStyleResize
          && currentFontSize === Number.parseFloat(this.suppressedFontSize)
        ) {
          this.suppressNextStyleResize = false;
          this.suppressedFontSize = null;
          return;
        }
        this.handleTextAdjust();
      },
    },
  },

  created() {
    if (this._fontSize === null) {
      // fix any font sizes that got borked
      this._fontSize = 40;
    }
    // modernize previously created text elements
    // we have other elements using this (like social-text) so limit scope
    if (this.element.type === 'text' && this.element.version === 'v1') {
      this.element.styles.textFlowOption = 2;
      this.element.styles.textBoxOption = 1;
      if (this.element.fitText) {
        this.element.styles.maxSizeText = this.fontSize;
      }

      this.element.version = 'v2';
    }

    this.previousFontSize = this.fontSize;

    this.resizeText = debounce(this.resizeText, 500);
  },

  mounted() {
    EventBus.on('text-adjusted', this.handleTextAdjust);
    EventBus.on('draggable-resized', this.handleExternalResize);
    EventBus.on('set-size', this.handleExternalResize);
    EventBus.on('resize-text', this.handleExternalResize);
    EventBus.on('fonts-loaded', this.resizeText);

    if (this.$refs.editor) {
      this.$refs.editor.addEventListener('paste', this.onPaste);
    }

    this.resizeText(true);
  },

  beforeUnmount() {
    if (this.resizeText?.cancel) {
      this.resizeText.cancel();
    }

    EventBus.off('text-adjusted', this.handleTextAdjust);
    EventBus.off('draggable-resized', this.handleExternalResize);
    EventBus.off('set-size', this.handleExternalResize);
    EventBus.off('resize-text', this.handleExternalResize);
    EventBus.off('fonts-loaded', this.resizeText);

    if (this.$refs.editor) {
      this.$refs.editor.removeEventListener('paste', this.onPaste);
    }
  },

  methods: {
    onPaste(e) {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      document.execCommand('insertHTML', false, text);
      this.$nextTick(() => {
        this.onEditorChange({ target: e.target });
      });
    },

    sanitizeHtml(html) {
      const div = document.createElement('div');
      div.innerHTML = html;
      div.querySelectorAll('span[style]').forEach((span) => {
        span.replaceWith(...span.childNodes);
      });
      return div.innerHTML;
    },

    isMapped(property) {
      return this.isPropertyMapped(this.element.id, property);
    },

    onMouseUp(e) {
      if (this.isEditing) {
        e.stopPropagation();
      }
    },

    onBlur(e) {
      this.isEditing = false;
      this.onEditorChange(e);
      setTimeout(() => {
        e.target.innerHTML = this.text;
        this.content = this.text;
        this.resizeText();
      });
    },

    onFocus() {
      this.isEditing = true;

      setTimeout(() => {
        // Highlight all text when focusing text field
        document.execCommand('selectAll', false, null);
      });
    },

    onKeyDown(e) {
      if (e.key === 'Backspace') {
        // Check if cursor is at the beginning of a <li>
        const sel = window.getSelection();
        const node = sel.anchorNode || sel.focusNode;
        const parentNode = node?.parentNode;

        if (
          sel.anchorOffset === 0
          && ['LI', 'OL', 'UL'].includes(parentNode?.nodeName)
        ) {
          e.preventDefault();
          e.stopPropagation();

          let listEl;
          let listItemEl;

          if (parentNode.nodeName === 'LI') {
            listItemEl = parentNode;
            listEl = listItemEl.parentNode;
          } else {
            listEl = parentNode;
            listItemEl = listEl.firstChild;
          }

          const textEl = document.createTextNode(listItemEl.textContent);

          if (listItemEl.previousSibling && listItemEl.nextSibling) {
            // Split list in half
            // NodeLists are dumb btw
            const { children } = listEl;
            const idx = Array.prototype.indexOf.call(children, listItemEl);

            const ch1 = Array.prototype.slice.call(children, 0, idx);
            const ch2 = Array.prototype.slice.call(children, idx + 1);

            const list1 = document.createElement(listEl.nodeName);
            const list2 = document.createElement(listEl.nodeName);

            ch1.forEach((ch) => {
              list1.appendChild(ch);
            });
            ch2.forEach((ch) => {
              list2.appendChild(ch);
            });

            listEl.replaceWith(list1);
            list1.after(textEl);
            textEl.after(list2);
          } else if (listItemEl.previousSibling) {
            // Remove <li> and add content after list
            listEl.removeChild(listItemEl);
            listEl.after(textEl);
          } else if (listItemEl.nextSibling) {
            // Remove <li> and add content before list
            listEl.removeChild(listItemEl);
            listEl.before(textEl);
          } else {
            // Delete the whole list
            listEl.replaceWith(textEl);
          }

          // Set caret at beginning of line
          resetCursor(sel, textEl);
        }
      }
    },

    onEditorChange(e) {
      const text = e.target.innerText;
      let html = e.target.innerHTML;

      // Check for special characters to replace (e.g. "- " turns into an <ul>)
      if (e.inputType === 'insertText' && e.data === ' ') {
        const trimLeft = (str) => str.replace(/^\s+/, '');
        const sel = window.getSelection();
        const node = sel.anchorNode || sel.focusNode;
        const data = trimLeft(node.textContent || '');

        if (/^-\s/.test(data) || /^\d+\.\s/.test(data)) {
          // Replace current line with list element
          const listType = data.indexOf('-') === 0 ? 'ul' : 'ol';
          const contents = data.slice(2);
          const parent = node.parentNode;
          const listEl = document.createElement(listType);
          const li = document.createElement('li');
          li.innerText = trimLeft(contents);
          listEl.appendChild(li);
          parent.insertBefore(listEl, node);
          parent.removeChild(node);

          // Set caret at beginning of list item
          resetCursor(sel, li);
        }
      }

      // Merge list nodes that are next to each other
      e.target.childNodes.forEach((node) => {
        const child = node;
        const sibling = child.nextSibling;

        if (!child.querySelector) {
          return;
        }

        const listEl = ['UL', 'OL'].includes(child.nodeName)
          ? child
          : child.querySelector('ul') || child.querySelector('ol');

        if (listEl && sibling) {
          const listType = listEl.nodeName.toLowerCase();
          const siblingListEl = ['UL', 'OL'].includes(sibling.nodeName)
            ? sibling
            : sibling?.querySelector
              && (sibling?.querySelector('ul') || sibling?.querySelector('ol'));

          if (
            siblingListEl
            && listType === siblingListEl.nodeName.toLowerCase()
          ) {
            // Merge!
            listEl.innerHTML += siblingListEl.innerHTML;
            siblingListEl.parentNode.removeChild(siblingListEl);
          }
        }
      });

      const { textContent } = e.target;
      const lineBreakCount = (text.match(/\n/g) || []).length;
      const len = textContent.length + lineBreakCount;

      // enforce character length
      if (
        this.element.limitCharacters
        && len > this.element.maxCharacterLength
      ) {
        // Clone contenteditable so contents aren't edited in place
        const clone = e.target.cloneNode(true);
        const nodes = [];
        clone.childNodes.forEach((child) => {
          nodes.unshift(child);
        });

        const textCount = nodes.reduce(
          (accum, node) => accum + node.textContent.length,
          0,
        );
        let overflow = textCount - this.element.maxCharacterLength;

        for (let i = 0; i < nodes.length; i += 1) {
          const node = nodes[i];

          const t = node.textContent;

          if (t.length >= overflow) {
            // Slice up inner text and break out
            node.textContent = t.substr(0, t.length - overflow);
            break;
          }

          // Clear text of node, and continue to the next one
          overflow -= t.length;
          node.textContent = '';
        }

        html = clone.innerHTML;
      }

      this.$emit('update:text', this.sanitizeHtml(html));

      // Skip everything if no text
      if (!text.trim()) {
        return;
      }

      if (!this.element.limitCharacters || !this.checkOverCharLimit()) {
        this.resizeText();
      }
    },
    checkOverCharLimit() {
      if (this.characterCount > this.element.maxCharacterLength) {
        return true;
      }

      return false;
    },
    handleTextAdjust() {
      if (this.shouldAutoResize) {
        this.resizeText();
      }
    },
    handleExternalResize(eventData) {
      const elementDataListId = this.element?.data?.dataListId;
      if (elementDataListId) {
        if (!eventData?.dataListId || eventData.dataListId !== elementDataListId) {
          return;
        }
      }

      if (this.shouldAutoResize) {
        this.resizeText();
      }
    },
    getTextWidth() {
      return [...this.editorEl.childNodes].reduce(
        (acc, child) => {
          if (child.offsetWidth > acc.width) {
            acc.width = child.offsetWidth;
            acc.length = child.textContent.length;
          }
          return acc;
        },
        { width: 0, length: 0 },
      );
    },
    getTextHeight() {
      return [...this.editorEl.childNodes].reduce(
        (acc, child) => acc + child.offsetHeight,
        0,
      );
    },
    scaleText() {
      if (!this.editorEl.textContent || (!this.element.text && !this.isMapped('text'))) {
        return;
      }

      this.fineTuneIterations = 0;

      // adapted from here: https://github.com/BrOrlandi/big-text.js/blob/master/big-text.js
      const { width: styleWidth, height: styleHeight } = this.element.styles;
      const width = styleWidth - this.xPadding;
      const height = styleHeight - this.yPadding;
      const fontSizeFactor = 0.8;

      // set styles to get sizes of just text
      this.editorEl.style.fontSize = `${1000 * fontSizeFactor}px`;
      const children = this.editorEl.childNodes;

      children.forEach((el) => {
        const child = el;

        if (child.nodeName === '#text') {
          if (!child.textContent.trim()) {
            this.editorEl.removeChild(child);
            return;
          }

          const element = document.createElement('div');
          this.editorEl.insertBefore(element, child);
          element.innerText = child.textContent;
          this.editorEl.removeChild(child);
          element.style.alignSelf = 'flex-start';
          return;
        }

        child.style.alignSelf = 'flex-start';
      });

      // Strip out extra <br>
      const lastChild = children[children.length - 1];
      if (lastChild?.nodeName === 'BR') {
        this.editorEl.removeChild(lastChild);
      }

      // find widest child
      const childData = this.getTextWidth();

      // total children height
      const { letterSpacing } = this.element.styles;
      const childrenHeight = this.getTextHeight();
      const widthFactor = (width - childData.length * letterSpacing) / childData.width;
      const heightFactor = height / childrenHeight;

      let lineHeight;

      if (this.element.styles.textFlowOption === 2) {
        // DO things differently because of wrapping
        // TODO: this could be a more accurate guess
        const hyp1 = Math.sqrt(width ** 2 + height ** 2);
        const hyp2 = Math.sqrt(childData.width ** 2 + childrenHeight ** 2);

        const hypFactor = hyp1 / hyp2;

        lineHeight = Math.floor(hypFactor * 1000);
      } else if (heightFactor > widthFactor) {
        lineHeight = Math.floor(widthFactor * 1000);
      } else {
        lineHeight = Math.floor(heightFactor * 1000);
      }

      const fontSize = Math.floor(lineHeight * fontSizeFactor);

      if (fontSize === Infinity || fontSize === 0) {
        // jump if we can't calculate so the data doesn't get borked
        // this happens when the element is hidden from the canvas
        this.makingChanges = false;
        return;
      }

      const initialFontSize = Math.max(1, fontSize);
      this.editorEl.style.fontSize = `${initialFontSize}px`;

      const { maxSizeText } = this.element.styles;
      // Sometimes the size is slightly off but close so this fine tunes it
      const fineTune = () => {
        const normalizeSize = (size) => {
          const numericSize = Number.parseFloat(size);
          if (!Number.isFinite(numericSize)) {
            return 1;
          }

          return Math.max(1, Math.floor(numericSize));
        };
        let currentSize = normalizeSize(initialFontSize);
        const getTextMetrics = () => [...this.editorEl.childNodes].reduce(
          (acc, child) => {
            const childWidth = child.offsetWidth || 0;
            if (childWidth > acc.width) {
              acc.width = childWidth;
            }

            acc.height += child.offsetHeight || 0;
            return acc;
          },
          { width: 0, height: 0 },
        );
        const clearChildAlignment = () => {
          children.forEach((child) => {
            child.style.removeProperty('align-self');
          });
        };
        const finish = (finalSize = currentSize) => {
          const normalizedSize = normalizeSize(finalSize);
          const nextFontSize = Number.isFinite(normalizedSize)
            ? normalizedSize
            : 1;
          const fontSizeChanged = this._fontSize !== nextFontSize;

          this.suppressNextStyleResize = fontSizeChanged;
          this.suppressedFontSize = fontSizeChanged ? nextFontSize : null;
          this._fontSize = nextFontSize;
          this.editorEl.style.removeProperty('font-size');
          clearChildAlignment();
          this.makingChanges = false;
        };
        const setAndMeasure = (size) => {
          currentSize = normalizeSize(size);
          this.editorEl.style.fontSize = `${currentSize}px`;
          this.fineTuneIterations += 1;

          if (this.fineTuneIterations >= this.fineTuneMaxIterations) {
            return { tooWide: false, tooTall: false, hitIterationLimit: true };
          }

          const textMetrics = getTextMetrics();

          return {
            tooWide: textMetrics.width > width,
            tooTall: textMetrics.height > height,
            hitIterationLimit: false,
          };
        };

        // Prevent infinitely small behavior
        if (width <= 1 || height <= 1) {
          finish(1);
          return;
        }

        const maxAutoSize = 10000;
        const maxSizeLimit = maxSizeText > 0
          ? normalizeSize(maxSizeText)
          : maxAutoSize;

        let fitSize = 0;
        let overflowSize = null;
        let startSize = normalizeSize(initialFontSize);
        startSize = Math.min(startSize, maxSizeLimit);

        let measured = setAndMeasure(startSize);
        if (measured.hitIterationLimit) {
          finish();
          return;
        }

        if (!measured.tooWide && !measured.tooTall) {
          fitSize = startSize;

          if (fitSize >= maxSizeLimit) {
            finish(fitSize);
            return;
          }

          let probe = fitSize;
          while (probe < maxSizeLimit) {
            const nextProbe = Math.min(maxSizeLimit, Math.max(probe + 1, probe * 2));
            measured = setAndMeasure(nextProbe);

            if (measured.hitIterationLimit) {
              finish(fitSize);
              return;
            }

            if (measured.tooWide || measured.tooTall) {
              overflowSize = nextProbe;
              break;
            }

            fitSize = nextProbe;
            probe = nextProbe;
          }

          if (!overflowSize) {
            finish(fitSize);
            return;
          }
        } else {
          overflowSize = startSize;
          measured = setAndMeasure(1);

          if (measured.hitIterationLimit) {
            finish(1);
            return;
          }

          if (measured.tooWide || measured.tooTall) {
            finish(1);
            return;
          }

          fitSize = 1;
        }

        if (!overflowSize) {
          finish(fitSize);
          return;
        }

        let low = fitSize;
        let high = overflowSize;

        while (low + 1 < high) {
          const mid = Math.floor((low + high) / 2);
          measured = setAndMeasure(mid);

          if (measured.hitIterationLimit) {
            finish(low);
            return;
          }

          if (measured.tooWide || measured.tooTall) {
            high = mid;
          } else {
            low = mid;
          }
        }

        finish(low);
      };

      this.makingChanges = true;
      fineTune();
    },
    scaleContainer() {
      const { width: styleWidth } = this.element.styles;

      this.editorEl.childNodes?.forEach((el) => {
        const child = el;

        if (child.nodeName === '#text') {
          if (!child.textContent.trim()) {
            return;
          }
          const element = document.createElement('div');
          this.editorEl.insertBefore(element, child);
          element.appendChild(child);
          element.style.alignSelf = 'flex-start';
          return;
        }

        child.style.alignSelf = 'flex-start';
      });

      // Resize container if not wrapping, otherwise preserve width and only scale height
      const width = this.element.styles.textFlowOption === 1
        ? this.getTextWidth().width
        : styleWidth - this.xPadding;

      const height = this.getTextHeight();

      // check if overflowing right side and move it over
      if (this.element.styles.left + width > this.getCanvas.width) {
        this.element.styles.left = this.getCanvas.width - width;

        EventBus.emit('set-position', {
          id: this.element.id,
          top: this.element.styles.top,
          left: this.getCanvas.width - width,
          force: true,
        });
      }

      // set styles
      this.element.styles.width = width + this.xPadding;
      this.element.styles.height = height + this.yPadding;

      // disable listener to avoid infinite loop, emit, then reenable
      EventBus.off('set-size', this.handleExternalResize);
      this.makingChanges = true;
      this.$nextTick(() => {
        this.editorEl.childNodes.forEach((child) => {
          if (child.nodeName === '#text') {
            if (!child.textContent.trim()) {
              return;
            }
            const element = document.createElement('div');
            this.editorEl.insertBefore(element, child);
            element.appendChild(child);
            element.style.alignSelf = 'flex-start';
            return;
          }

          child.style.removeProperty('align-self');
        });

        EventBus.emit('set-size', {
          width,
          height,
          force: true,
          id: this.element.id,
        });

        this.$nextTick(() => {
          EventBus.on('set-size', this.handleExternalResize);
          this.makingChanges = false;
        });
      });
    },
    resizeText(fromMounted = false) {
      if (this.makingChanges) return; // prevent infinite loops if resizing still going on

      const isTextSizingActive = this.element.fitText || this.element.styles.textBoxOption === 2;
      if (!isTextSizingActive) return;

      this.$nextTick(() => {
        if (this.element.fitText) {
          // Text Flow
          // wrap/no wrap is handled by "noWrapClass" above
          this.scaleText();
        } else if (this.element.styles.textBoxOption === 2) {
          // Text Box
          // fixed height/width should just work without any changes
          this.scaleContainer();
        }

        if (!fromMounted) {
          this.$emit('resizing');
        }
      });
    },
  },
};
</script>
